import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";

// Transform service data from backend to frontend format
const transformServiceToFrontend = (service) => ({
  id: service.id,
  service_name: service.name,
  name: service.name,
  description: service.description,
  category: service.category,
  total_duration_minutes: service.totalDurationMinutes,
  duration_minutes: service.totalDurationMinutes,
  base_price: service.basePrice,
  price: service.basePrice,
  requires_doctor: service.requiresDoctor,
  requires_room: service.requiresRoom,
  status: service.status,
  created_at: service.createdAt,
  updated_at: service.updatedAt,
  created_by: service.createdBy,
  practice_id: service.practiceId,
});

// Transform service data from frontend to backend format
const transformServiceToBackend = (service) => ({
  name: service.service_name || service.name,
  description: service.description,
  category: service.category,
  totalDurationMinutes:
    service.total_duration_minutes || service.duration_minutes,
  basePrice: service.base_price || service.price,
  requiresDoctor: service.requires_doctor,
  requiresRoom: service.requires_room,
  status: service.status || "active",
});

// Query Keys
export const serviceKeys = {
  all: ["services"],
  list: (filters) => ["services", "list", filters],
  detail: (id) => ["services", "detail", id],
  types: () => ["services", "types"],
};

// Get All Services Hook
export const useServices = (filters = {}) => {
  return useQuery({
    queryKey: serviceKeys.list(filters),
    queryFn: async () => {
      const response = await apiClient.get("/api/services", filters);
      return response.map(transformServiceToFrontend);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - services don't change often
  });
};

// Get Services by Practice Hook - for patients to filter services by selected practice
export const useServicesByPractice = (practiceId) => {
  return useQuery({
    queryKey: serviceKeys.list({ practiceId }),
    queryFn: async () => {
      const response = await apiClient.get("/api/services", { practiceId });
      return response.map(transformServiceToFrontend);
    },
    enabled: !!practiceId,
    staleTime: 10 * 60 * 1000, // 10 minutes - services don't change often
  });
};

// Get Service by ID Hook
export const useService = (serviceId) => {
  return useQuery({
    queryKey: serviceKeys.detail(serviceId),
    queryFn: async () => {
      const response = await apiClient.get(`/api/services/${serviceId}`);
      return transformServiceToFrontend(response);
    },
    enabled: !!serviceId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get Service Types Hook
export const useServiceTypes = () => {
  return useQuery({
    queryKey: serviceKeys.types(),
    queryFn: async () => {
      return await apiClient.get("/api/services/types");
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - types rarely change
  });
};

// Create Service Mutation
export const useCreateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceData) => {
      const transformedData = transformServiceToBackend(serviceData);
      const response = await apiClient.post("/api/services", transformedData);
      return transformServiceToFrontend(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
    },
  });
};

// Update Service Mutation
export const useUpdateService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceId, serviceData }) => {
      const transformedData = transformServiceToBackend(serviceData);
      const response = await apiClient.put(
        `/api/services/${serviceId}`,
        transformedData
      );
      return transformServiceToFrontend(response);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      queryClient.invalidateQueries({
        queryKey: serviceKeys.detail(variables.serviceId),
      });
    },
  });
};

// Delete Service Mutation
export const useDeleteService = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceId) => {
      return await apiClient.delete(`/api/services/${serviceId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
    },
  });
};
